package com.klantenstop.web.pages.authentication.login

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import com.materialkobweb.components.widgets.PrimaryTextButtonLinkStyle
import com.materialkobweb.components.widgets.TextButtonLink
import com.klantenstop.web.navigation.Navigation
import com.klantenstop.web.navigation.unauthenticatedGuard
import com.klantenstop.web.pages.authentication.components.AuthenticationContainer
import com.klantenstop.web.pages.authentication.registration.authLinkModifier
import com.materialkobweb.styles.MaterialColorVars
import com.klantenstop.web.utils.Res
import com.klantenstop.web.utils.decodeReturnUrl
import com.klantenstop.web.utils.format
import com.materialkobweb.utils.rememberViewModel
import com.klantenstop.web.utils.withAlpha
import com.materialkobweb.components.DsPageLayout
import com.materialkobweb.components.widgets.DsEditableField
import com.materialkobweb.components.widgets.FilledButton
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text

@Page("/inloggen")
@Composable
fun LoginPage() = unauthenticatedGuard {
    val pageContext = rememberPageContext()

    // Use constant key to maintain ViewModel across navigation
    val viewModel = rememberViewModel(cached = false) { LoginViewModel() }
    val state by viewModel.state.collectAsState()

    // Get the terugNaar query parameter and decode from base64
    val returnTo = pageContext.route.params["terugNaar"]?.let { encodedUrl ->
        decodeReturnUrl(encodedUrl)
    } ?: Navigation.Screen.Home.route

    LaunchedEffect(Unit) {
        viewModel.effect.collect { event ->
            when (event) {
                LoginContract.Effect.NavigateToNextScreen -> {
                    // Navigate to the returnTo URL if it exists, otherwise go to home
                    pageContext.router.navigateTo(returnTo, UpdateHistoryMode.REPLACE)
                }
            }
        }
    }

    LoginPageContent(
        returnToUrl = returnTo,
        state = state,
        sendIntent = viewModel::sendIntent
    )
}

@Composable
private fun LoginPageContent(
    returnToUrl: String,
    state: LoginContract.State,
    sendIntent: (LoginContract.Intent) -> Unit
) {
    DsPageLayout(
        modifier = Modifier.fillMaxSize(),
        title = Res.String.DocumentTitle.format("Login"),
        header = {},
        overflowMenu = {},
        overflowMenuOpened = false,
        footer = {},
        sidePanel = {},
    ) {
        AuthenticationContainer(
            title = "Welkom terug!",
            content = {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(16.px, Alignment.CenterVertically)
                ) {
                    DsEditableField(
                        modifier = Modifier.fillMaxWidth(),
                        id = "inputEmail",
                        label = "E-mailadres",
                        placeholder = "E-mailadres",
                        value = state.emailAddress,
                        valid = !state.hasAttemptedLogin || state.emailAddress.isNotBlank(),
                        onValueChange = { sendIntent(LoginContract.Intent.UpdateEmail(it)) },
                        required = true,
                        backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                        focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
                    )

                    Column(
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        DsEditableField(
                            modifier = Modifier.fillMaxWidth(),
                            id = "inputPassword",
                            label = "Wachtwoord",
                            placeholder = "Wachtwoord",
                            value = state.password,
                            valid = !state.hasAttemptedLogin || state.password.isNotBlank(),
                            onValueChange = { sendIntent(LoginContract.Intent.UpdatePassword(it)) },
                            type = InputType.Password,
                            required = true,
                            backgroundColor = MaterialColorVars.SurfaceContainer.value(),
                            focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
                        ) { sendIntent(LoginContract.Intent.Login) }

                        SpanText(
                            text = "Minimum 8 tekens waarvan minstens 1 hoofdletter, 1 cijfer en 1 symbool",
                            modifier = Modifier
                                .margin(left = 8.px)
                                .fontSize(12.px)
                                .lineHeight(14.px)
                                .color(MaterialColorVars.Outline.value())
                        )
                    }

                    TextButtonLink(
                        modifier = Modifier.margin(left = (-8).px),
                        variant = PrimaryTextButtonLinkStyle,
                        size = ButtonSize.SM,
                        path = if (state.emailAddress.isNotEmpty()) {
                            "${Navigation.Screen.ForgotPassword.route}?email=${state.emailAddress}"
                        } else {
                            Navigation.Screen.ForgotPassword.route
                        },
                        updateHistoryMode = UpdateHistoryMode.REPLACE
                    ) {
                        Text("Wachtwoord vergeten?")
                    }
                }

                FilledButton(
                    modifier = Modifier.fillMaxWidth(),
                    type = ButtonType.Submit,
                    onClick = { sendIntent(LoginContract.Intent.Login) }
                ) {
                    Text("Log in")
                }
            },
            footer = {
                Text("Ben je nog niet bekend bij ons? Maak dan eerst een nieuw account aan ")
                Link(
                    modifier = authLinkModifier,
                    path = Navigation.Screen.Register(returnToUrl).route,
                    updateHistoryMode = UpdateHistoryMode.REPLACE,
                    text = "voor jezelf"
                )
                // TODO: not possible for now, but maybe in the future we want to allow users to create an account for their company without having to create a personal account first.
                //  In that case we can add this link back and create a separate registration page for companies.
//                Text(" of ")
//                Link(
//                    modifier = authLinkModifier,
//                    path = Navigation.Screen.RegisterCompany.route,
//                    updateHistoryMode = UpdateHistoryMode.REPLACE,
//                    text = "je bedrijf"
//                )
            }
        )
    }
}
